import React from 'react';
import type { SocialOpportunityItem } from '@inference.engine/widgets/web';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Button,
} from '@monksflow/monks-ui';
import { X } from 'lucide-react';
import {
  AudienceTags,
  displayRegion,
  InsightTypeBadge,
  OpportunityTypeBadge,
} from '@/entities/opportunity';
import { SparkSentiment } from '@/entities/persona-spark/model/types';
import { SparkCard } from '@/entities/persona-spark/ui/SparkCard/SparkCard';
import { SocialMediaOpportunitiesDeps } from '@/features/social-media-opportunities-widget';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { formatRelativeDate } from '../../../../../shared/utils/dateUtils';
import { useSocialMediaOpportunitiesStore } from '../../../model/social-media-opportunities-widget.store';
import { BrandMentionsSection } from '../BrandMentionsSection';
import { DurationTypeCard } from '../DurationType/DurationTypeCard';
import { HowToRespond } from '../HowToRespond';
import { PotentialImpactCard } from '../PotentialImpactCard';
import { WhyItMatters } from '../WhyItMatters';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';
import { personaLabel } from '@/shared/lib/persona-label';

interface OpportunityDetailProps {
  /**
   * Optional item to display. If provided, bypasses the global store for more
   * efficient direct prop flow (e.g., in OpportunitiesRoom overlay).
   * If omitted, falls back to reading from the store (e.g., in
   * SocialMediaOpportunitiesWidget sidebar).
   */
  item?: SocialOpportunityItem | null;
  /**
   * Cross-feature deps injected via dependency injection.
   * Wired by the config-layer factory at `@/lib/config/agents.config.ts`.
   * Bundles persona-sparks (`SparkCard`, `mapAudienceProfileToSpark`,
   * `useStartInterview`) and chats (`launchAgentWithWidget`,
   * `sendOrCreateChat`) helpers.
   *
   * OPTIONAL so legacy direct mounts (snapshot tests) keep compiling;
   * when omitted, the persona-sparks SparkCard area is hidden and the
   * "start interview" CTA is a no-op.
   */
  deps?: Partial<
    Pick<
      SocialMediaOpportunitiesDeps,
      'SparkCard' | 'mapAudienceProfileToSpark' | 'useStartInterview'
    >
  >;
  /**
   * Callback to close the opportunity detail overlay.
   * If provided, takes precedence over the default store-based close behavior.
   */
  onClose?: () => void;
  /**
   * Library preview mount: hide the close (X) control and the persona-spark
   * "start interview" CTA (it navigates away). All read content stays visible.
   */
  readOnly?: boolean;
  /**
   * Print/export view (e.g. the standalone `/download/opportunity/:id` route that
   * gets printed to PDF). Renders the content fully expanded: all sources (no
   * "More Posts" collapse), full audience tags (no "+N"/truncation), and hides
   * interactive-only chrome (filter tabs, the "start chat" CTA).
   */
  exporting?: boolean;
}

// Getters, not values: a resolved label here would freeze English at import.
const CONFIDENCE_CONFIG = {
  high: {
    get label() {
      return i18n.t(
        'analysis:socialMediaOpportunitiesWidget.opportunityDetail.confidence.positive',
      );
    },
  },
  medium: {
    get label() {
      return i18n.t('analysis:socialMediaOpportunitiesWidget.opportunityDetail.confidence.neutral');
    },
  },
  low: {
    get label() {
      return i18n.t(
        'analysis:socialMediaOpportunitiesWidget.opportunityDetail.confidence.negative',
      );
    },
  },
};

const getSentiment = (quoteConfidence: string): SparkSentiment | undefined => {
  // Create a normalized lookup map (cache this if performance becomes an issue)
  const normalizedMap = new Map(
    Object.entries(CONFIDENCE_CONFIG).map(([key, value]) => [
      key.toLowerCase().replace(/[^a-z0-9]/g, ''), // Remove special chars
      value,
    ]),
  );

  const normalizedInput = quoteConfidence.toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalizedMap.get(normalizedInput)?.label as SparkSentiment;
};

const useStartInterviewFallback = () => ({ startInterview: undefined });

export const OpportunityDetail = ({
  item,
  onClose,
  deps,
  readOnly,
  exporting = false,
}: OpportunityDetailProps = {}): React.JSX.Element => {
  const { t } = useTranslation('analysis');
  const storeItem = useSocialMediaOpportunitiesStore((state) => state.selectedItem);
  // Prop takes precedence over store for direct prop flow efficiency
  const selectedItem = item ?? storeItem;

  const useStartInterviewHook = deps?.useStartInterview ?? useStartInterviewFallback;
  const { startInterview } = useStartInterviewHook();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      // Default behavior: clear the store
      useSocialMediaOpportunitiesStore.getState().setSelectedItem(null);
    }
  };

  const { downloadUrl: thumbnailUrl } = useGetDownloadUrl(selectedItem?.thumbnail?.assetId);

  if (!selectedItem) {
    return <div>{t('socialMediaOpportunitiesWidget.opportunityDetail.noOpportunitySelected')}</div>;
  }

  const firstAudienceProfile = selectedItem.targetAudienceProfiles?.[0];
  const audienceTagList = [
    ...new Set(selectedItem.targetAudienceProfiles?.map((profile) => profile.segmentName) ?? []),
  ];

  return (
    <div className="relative flex flex-col gap-16 pt-8 pb-12 max-w-300 mx-auto px-8">
      {!readOnly && (
        <div className="absolute top-4 right-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="size-8 p-0 text-muted-foreground hover:text-foreground"
                  aria-label={t('socialMediaOpportunitiesWidget.opportunityDetail.close')}
                >
                  <X className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('socialMediaOpportunitiesWidget.opportunityDetail.close')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <div className={thumbnailUrl ? 'grid grid-cols-2 gap-8' : ''}>
          {thumbnailUrl && (
            <div className="flex max-h-102 items-start justify-center overflow-hidden rounded-lg">
              <img
                src={thumbnailUrl}
                alt={selectedItem.opportunityTitle}
                className="w-full h-full rounded-lg object-cover"
              />
            </div>
          )}
          <div className="flex flex-col gap-2 justify-between">
            <div>
              <div className="flex mb-4">
                <div className="mr-3">
                  {selectedItem.durationType?.value && (
                    <OpportunityTypeBadge type={selectedItem.durationType.value} />
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs text-muted-foreground">
                    {formatRelativeDate(selectedItem.createdAt)}
                  </p>
                  {displayRegion(selectedItem.region) && (
                    <>
                      <span className="h-4 w-0.5 bg-border-lighter" />

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {displayRegion(selectedItem.region)}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('socialMediaOpportunitiesWidget.opportunityDetail.region')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </>
                  )}
                </div>
              </div>
              <p className="text-3xl font-medium text-foreground">
                {selectedItem.opportunityTitle}
              </p>
            </div>
            <div>
              {selectedItem.insightType && (
                <div className="flex justify-between">
                  <p className="text-xs text-muted-foreground">
                    {t('socialMediaOpportunitiesWidget.opportunityDetail.insightType')}
                  </p>
                  <InsightTypeBadge type={selectedItem.insightType} />
                </div>
              )}
              <hr className="mt-3 mb-3 w-full border-t border-border-lighter" />
              {audienceTagList.length > 0 && (
                <div className="flex justify-between">
                  <p className="text-xs text-muted-foreground">
                    {t('socialMediaOpportunitiesWidget.opportunityDetail.audience')}
                  </p>
                  <AudienceTags audiences={audienceTagList} exporting={exporting} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={selectedItem.durationType ? 'grid grid-cols-2 gap-6' : ''}>
          <WhyItMatters data={selectedItem} />
          {selectedItem.durationType && <DurationTypeCard data={selectedItem.durationType} />}
        </div>

        {firstAudienceProfile && (
          <SparkCard
            spark={{
              persona: {
                id: firstAudienceProfile.id || '',
                name: firstAudienceProfile.name || '',
                age: firstAudienceProfile.age || 0,
                role: firstAudienceProfile.role || '',
                assetId: firstAudienceProfile.photoAssetId!,
              },
              spark: firstAudienceProfile.quote || '',
              sentiment: getSentiment(firstAudienceProfile.quoteConfidence!) || 'Neutral',
              source: '',
            }}
            onStartChat={
              // No interactive "start chat" CTA in the export/print view.
              !readOnly && !exporting && startInterview
                ? () => {
                    const personaToTalkTo = personaLabel(firstAudienceProfile);
                    startInterview(
                      firstAudienceProfile.id || '',
                      `I want to speak with ${personaToTalkTo}`,
                      firstAudienceProfile,
                      selectedItem,
                    );
                  }
                : undefined
            }
          />
        )}

        <HowToRespond
          ideas={selectedItem.ideationData?.ideas}
          executions={selectedItem.ideationData?.ideas[0]?.executions}
          opportunityTypeImpact={selectedItem.durationType?.impact}
        />

        <PotentialImpactCard predictedOutcome={selectedItem.predictedOutcome} />

        <BrandMentionsSection data={selectedItem} exporting={exporting} />
      </div>
    </div>
  );
};
